import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import notification from "common/utils/notification"
import { useTranslation } from "react-i18next"
import { InsertEtiquetaCommandProps } from "./Commands/InsertEtiquetaCommand"
import { UpdateEtiquetaCommandProps } from "./Commands/UpdateEtiquetaCommand"
import {
  GetEtiquetasEmpresaQueryParamsProps,
  deleteEtiquetaEmpresaById,
  getEtiquetasEmpresa,
  postEtiquetaEmpresa,
  putEtiquetaEmpresa,
} from "./Etiqueta.service"

export function useEtiquetasEmpresa(
  queryParams?: GetEtiquetasEmpresaQueryParamsProps
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    data: etiquetasEmpresa,
    isLoading: etiquetasEmpresaIsLoading,
    error: etiquetasEmpresaError,
  } = useQuery({
    queryKey: ["getEtiquetasEmpresa", queryParams],
    queryFn: () => queryParams && getEtiquetasEmpresa(queryParams),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 2, // 2 hours
    retry: false,
  })
  const etiquetasEmpresaInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getEtiquetasEmpresa"] })

  const {
    mutateAsync: addEtiquetaEmpresa,
    isPending: etiquetasEmpresaIsLoadingAdd,
  } = useMutation({
    mutationFn: async (nuevaEtiqueta: InsertEtiquetaCommandProps) =>
      postEtiquetaEmpresa(nuevaEtiqueta).then(() =>
        etiquetasEmpresaInvalidation()
      ),
    onSuccess: () =>
      notification.success({
        body: `${t("notification.categoryCorrectlyCreated")}`,
      }),
  })

  const {
    mutateAsync: deleteEtiquetaEmpresa,
    isPending: etiquetasEmpresaIsLoadingDelete,
  } = useMutation({
    mutationFn: async (id: string) =>
      deleteEtiquetaEmpresaById(id).then(() => etiquetasEmpresaInvalidation()),
    onSuccess: () =>
      notification.success({
        body: `${t("notification.categoryCorrectlyDelete")}`,
      }),
  })

  const {
    mutateAsync: updateEtiquetaEmpresa,
    isPending: etiquetasEmpresaIsLoadingUpdate,
  } = useMutation({
    mutationFn: async (updateEtiqueta: UpdateEtiquetaCommandProps) =>
      putEtiquetaEmpresa(updateEtiqueta).then(() =>
        etiquetasEmpresaInvalidation()
      ),
    onSuccess: () =>
      notification.success({
        body: `${t("notification.categoryCorrectlyUpdate")}`,
      }),
  })

  return {
    etiquetasEmpresa,
    etiquetasEmpresaIsLoading,
    etiquetasEmpresaError,
    etiquetasEmpresaInvalidation,
    addEtiquetaEmpresa,
    etiquetasEmpresaIsLoadingAdd,
    deleteEtiquetaEmpresa,
    etiquetasEmpresaIsLoadingDelete,
    updateEtiquetaEmpresa,
    etiquetasEmpresaIsLoadingUpdate,
  }
}
